import { useEffect, useState, useRef } from "react";
import {
  Plus,
  Search,
  SlidersHorizontal,
  FileUp,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Tag,
  MapPin,
} from "lucide-react";
import api from "../../connector/api";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { CustomSelect } from "../components/common/CustomSelect";
import AppLayout from "../components/common/AppLagout";
import NewBookModal from "../components/NewBookModal";
import BulkUploadModal from "../components/BulkUploadModal";

export default function Cataloging() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [totalBooks, setTotalBooks] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [sortConfig] = useState({ key: "date_added", direction: "desc" });

  // 1. PAGE-LEVEL SEARCH STATE (Filters the local book grid)
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const totalPages = Math.ceil(totalBooks / itemsPerPage) || 1;

  // 2. HEADER-LEVEL SEARCH STATE (Independent dropdown search)
  const searchRef = useRef(null);
  const [headerSearchTerm, setHeaderSearchTerm] = useState("");
  const [headerSearchLoading, setHeaderSearchLoading] = useState(false);
  const [headerSearchResults, setHeaderSearchResults] = useState([]);
  const [headerShowDropdown, setHeaderShowDropdown] = useState(false);
  const [headerHighlightedIndex, setHeaderHighlightedIndex] = useState(-1);

  const [formData, setFormData] = useState({
    title: "",
    author: "",
    category: "",
    isbn: "",
    barcode: "",
    description: "",
    call_number: "",
    publication_year: "",
    publisher: "",
    status: "available",
  });

  const CATEGORY_CHOICES = [
    "Adult Fiction",
    "Adult Non-fiction",
    "Ellen G. White",
    "Flap Book",
    "Board Books",
    "DVD's",
    "Juvenile Non-fiction",
    "Young Adult",
    "Christmas Books",
    "Khmer",
    "Reference",
    "Story Collection",
    "Level Books",
    "Teacher Resources",
    "Biography",
    "Other",
  ];

  const STATUS_CHOICES = ["available", "borrowed", "lost", "removed"];

  const categoryOptions = [
    { label: "All genres", value: "" },
    ...CATEGORY_CHOICES.map((cat) => ({ label: cat, value: cat })),
  ];

  const statusOptions = [
    { label: "All statuses", value: "" },
    ...STATUS_CHOICES.map((st) => ({
      label: st.charAt(0).toUpperCase() + st.slice(1),
      value: st,
    })),
  ];

  const CATEGORY_ACCENT_COLORS = [
    "from-rose-950/80 to-amber-900/60 border-rose-700/40 text-rose-300",
    "from-sky-950/80 to-blue-900/60 border-sky-700/40 text-sky-300",
    "from-emerald-950/80 to-teal-900/60 border-emerald-700/40 text-emerald-300",
    "from-purple-950/80 to-indigo-900/60 border-purple-700/40 text-purple-300",
    "from-amber-950/80 to-orange-900/60 border-amber-700/40 text-amber-300",
    "from-stone-900 to-stone-800 border-stone-700/40 text-stone-300",
  ];

  // Debounce the Page Search input (for grid filtering)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Header Typeahead API Search (Independent of page grid)
  useEffect(() => {
    if (!headerSearchTerm.trim()) {
      setHeaderSearchResults([]);
      setHeaderShowDropdown(false);
      return;
    }

    setHeaderSearchLoading(true);
    const handler = setTimeout(async () => {
      try {
        const res = await api.get("/cataloging/dashboard/search", {
          params: { q: headerSearchTerm },
        });
        const data = res.data || [];
        const normalizedResults = data.map((b) => ({
          ...b,
          cover_url: b.cover_image?.startsWith("http")
            ? b.cover_image
            : b.cover_image
            ? `${api.defaults.baseURL.replace("/api", "")}${b.cover_image}`
            : null,
          is_available: b.status === "available",
        }));
        setHeaderSearchResults(normalizedResults);
        setHeaderShowDropdown(true);
      } catch (err) {
        console.error("Header search failed:", err);
        setHeaderSearchResults([]);
      } finally {
        setHeaderSearchLoading(false);
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [headerSearchTerm]);

  // Handle outside click to close header search dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setHeaderShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch Page Catalog Books
  const fetchBooks = async (isInitial = false) => {
    if (isInitial) setLoading(true);
    try {
      const res = await api.get("/cataloging/", {
        params: {
          page: currentPage,
          page_size: itemsPerPage,
          search: debouncedSearch,
          ordering: sortConfig.direction === "asc" ? sortConfig.key : `-${sortConfig.key}`,
          status: statusFilter || undefined,
          category: categoryFilter || undefined,
        },
      });
      setBooks(res.data.results || []);
      setTotalBooks(res.data.count || 0);
    } catch {
      toast.error("Failed to load books from inventory system");
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchBooks(true);
  }, []);

  // Background fetch when page or filters change
  useEffect(() => {
    fetchBooks(false);
  }, [currentPage, itemsPerPage, debouncedSearch, sortConfig, statusFilter, categoryFilter]);

  const handleAddBook = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cataloging/", formData);
      toast.success("Book added successfully");
      setModalOpen(false);
      setFormData({
        title: "",
        author: "",
        category: "",
        isbn: "",
        barcode: "",
        description: "",
        call_number: "",
        publication_year: "",
        publisher: "",
        status: "available",
      });
      fetchBooks(false);
    } catch {
      toast.error("Failed to append entry");
    }
  };

  const handleSelectHeaderBook = (book) => {
    setHeaderShowDropdown(false);
    setHeaderSearchTerm("");
    setHeaderHighlightedIndex(-1);
    navigate(`/library/cataloging/book/detail/${book.id}`);
  };

  return (
    <AppLayout
      headerProps={{
        title: "Book Cataloging",
        searchTerm: headerSearchTerm,
        setSearchTerm: setHeaderSearchTerm,
        searchRef,
        searchLoading: headerSearchLoading,
        searchResults: headerSearchResults,
        showDropdown: headerShowDropdown,
        setShowDropdown: setHeaderShowDropdown,
        highlightedIndex: headerHighlightedIndex,
        setHighlightedIndex: setHeaderHighlightedIndex,
        handleSelectBook: handleSelectHeaderBook,
      }}
    >
      <Toaster position="top-right" />

      <div className="space-y-5 pb-8">
        {/* TOP ACTION BAR & CONTROL DECK */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 bg-[#191613] p-3.5 rounded-2xl border border-[#26201a]">
          {/* Left Side: Page Search & Custom Dropdowns */}
          <div className="flex flex-wrap items-center gap-2.5 flex-1">
            {/* Page Search Box (Only filters catalog grid) */}
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <input
                type="text"
                placeholder="Search title, author, barcode..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-9 pr-3 py-2 bg-[#131110] border border-[#2e2720] rounded-xl text-xs font-medium text-stone-200 placeholder-stone-500 focus:outline-none focus:border-amber-500/50 transition"
              />
            </div>

            {/* Custom Category Dropdown */}
            <CustomSelect
              value={categoryFilter}
              onChange={(val) => {
                setCategoryFilter(val);
                setCurrentPage(1);
              }}
              options={categoryOptions}
              menuClassName="[scrollbar-width:thin] [scrollbar-color:#382f27_#131110] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-[#131110] [&::-webkit-scrollbar-thumb]:bg-[#382f27] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-amber-500"
            />

            {/* Custom Status Dropdown */}
            <CustomSelect
              value={statusFilter}
              onChange={(val) => {
                setStatusFilter(val);
                setCurrentPage(1);
              }}
              options={statusOptions}
              menuClassName="[scrollbar-width:thin] [scrollbar-color:#382f27_#131110] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-[#131110] [&::-webkit-scrollbar-thumb]:bg-[#382f27] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-amber-500"
            />

            {/* Total Indicator */}
            <div className="hidden sm:flex items-center text-xs font-mono text-stone-500 px-2 py-1 bg-[#131110] border border-[#231e18] rounded-lg">
              <span>{totalBooks} titles</span>
            </div>
          </div>

          {/* Right Side: Action Buttons */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setBulkModalOpen(true)}
              className="flex items-center gap-2 px-3.5 py-2 bg-[#201c17] hover:bg-[#28221c] text-stone-300 border border-[#2e2720] rounded-xl text-xs font-bold transition"
            >
              <FileUp className="w-3.5 h-3.5 text-stone-400" />
              <span>Bulk Import</span>
            </button>

            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-stone-950 rounded-xl text-xs font-bold transition shadow-md shadow-amber-500/10"
            >
              <Plus className="w-4 h-4 stroke-[2.5]" />
              <span>Add book</span>
            </button>
          </div>
        </div>

        {/* BOOK GRID CATALOGUE */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-44 bg-[#191613] rounded-2xl border border-[#26201a] animate-pulse" />
            ))}
          </div>
        ) : books.length === 0 ? (
          <div className="bg-[#191613] border border-[#26201a] rounded-2xl p-12 text-center space-y-3">
            <BookOpen className="w-10 h-10 text-stone-600 mx-auto" />
            <p className="text-sm font-bold text-stone-400">No catalogue holdings found</p>
            <p className="text-xs text-stone-600 max-w-sm mx-auto">
              No collection items match your active search or category filters. Try adjusting your search query.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {books.map((b, idx) => {
              const colorStyle = CATEGORY_ACCENT_COLORS[idx % CATEGORY_ACCENT_COLORS.length];
              const isAvailable = (b.status || "").toLowerCase().trim() === "available";
              const isBorrowed = (b.status || "").toLowerCase().trim() === "borrowed";

              return (
                <div
                  key={b.id}
                  onClick={() => navigate(`/library/cataloging/book/detail/${b.id}`)}
                  className="bg-[#191613] border border-[#26201a] hover:border-amber-500/30 rounded-2xl p-3.5 flex gap-3.5 transition group cursor-pointer relative overflow-hidden"
                >
                  <div
                    className={`w-28 shrink-0 rounded-xl bg-gradient-to-br ${colorStyle} border p-2.5 flex flex-col justify-between shadow-inner relative group-hover:scale-[1.02] transition duration-200`}
                  >
                    <div className="space-y-1">
                      <span className="text-[9px] font-black uppercase tracking-wider block opacity-75 truncate">
                        {b.category || "General"}
                      </span>
                      <p className="text-xs font-serif font-black leading-tight line-clamp-3 text-white">
                        {b.title}
                      </p>
                    </div>

                    <div className="mt-4 pt-2 border-t border-white/10">
                      <p className="text-[9px] font-serif text-white/70 truncate">{b.author || "Unknown"}</p>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-bold font-serif text-stone-100 group-hover:text-amber-400 transition truncate">
                          {b.title}
                        </h3>

                        <span
                          className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border capitalize ${
                            isAvailable
                              ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                              : isBorrowed
                              ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
                              : "bg-rose-500/10 text-rose-400 border-rose-500/20"
                          }`}
                        >
                          {b.status || "Available"}
                        </span>
                      </div>

                      <p className="text-xs text-stone-400 truncate mt-0.5">{b.author || "Unspecified Author"}</p>

                      <div className="flex flex-wrap items-center gap-1.5 mt-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#201c17] border border-[#2e2720] rounded-md text-[10px] font-mono text-stone-300">
                          <Tag className="w-2.5 h-2.5 text-amber-500" />
                          {b.category || "General"}
                        </span>

                        {b.publication_year && (
                          <span className="px-1.5 py-0.5 bg-[#201c17] border border-[#2e2720] rounded-md text-[10px] font-mono text-stone-400">
                            {b.publication_year}
                          </span>
                        )}

                        {b.call_number && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#201c17] border border-[#2e2720] rounded-md text-[10px] font-mono text-stone-400 truncate max-w-[120px]">
                            <MapPin className="w-2.5 h-2.5 text-stone-500" />
                            {b.call_number}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 pt-2.5 border-t border-[#231e18]">
                      <div className="flex items-center justify-between text-[10px] text-stone-500 mb-1">
                        <span>Copies status</span>
                        <span className="font-mono text-stone-300 font-bold">
                          {isBorrowed ? "1 / 1 out" : "0 / 1 out"}
                        </span>
                      </div>
                      <div className="w-full bg-[#131110] h-1.5 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${
                            isBorrowed ? "w-full bg-amber-500" : "w-0 bg-emerald-500"
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* PAGINATION TOOLBAR */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-[#191613] p-4 rounded-2xl border border-[#26201a] mt-6">
          <div className="flex items-center gap-3">
            <span className="text-xs text-stone-400 font-medium">
              Showing <span className="text-stone-100 font-bold">{Math.min(books.length, itemsPerPage)}</span> of{" "}
              <span className="text-stone-100 font-bold">{totalBooks}</span> catalog items
            </span>

            <div className="flex items-center gap-1.5 text-xs text-stone-500 font-bold ml-2">
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span>Per page:</span>
              <select
                className="bg-[#131110] border border-[#2e2720] rounded-lg px-2 py-0.5 font-bold text-stone-300 focus:outline-none"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[12, 24, 48, 96].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center bg-[#131110] border border-[#2e2720] rounded-xl text-stone-400 disabled:opacity-30 hover:text-amber-400 transition"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <span className="text-xs font-mono font-bold text-stone-300 px-3 py-1 bg-[#131110] border border-[#2e2720] rounded-xl">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-8 h-8 flex items-center justify-center bg-[#131110] border border-[#2e2720] rounded-xl text-stone-400 disabled:opacity-30 hover:text-amber-400 transition"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* NEW BOOK FORM MODAL */}
      {modalOpen && (
        <NewBookModal onClose={() => setModalOpen(false)} onSave={() => fetchBooks(false)}>
          <div className="p-1">
            <form onSubmit={handleAddBook} className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-bold text-stone-300">
              <div className="sm:col-span-2">
                <label className="block mb-1 text-stone-400">Book Title *</label>
                <input
                  className="w-full bg-[#131110] border font-serif tracking-wider border-[#2e2720] rounded-xl p-2.5 font-medium text-stone-100 focus:outline-none focus:border-amber-500/50 transition"
                  placeholder="e.g. Introduction to Data Science"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </div>

              <div>
                <label className="block mb-1 text-stone-400">Author Name *</label>
                <input
                  className="w-full bg-[#131110] border border-[#2e2720] rounded-xl p-2.5 font-medium text-stone-100 focus:outline-none focus:border-amber-500/50 transition"
                  placeholder="e.g. John Doe"
                  required
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                />
              </div>

              <div>
                <label className="block mb-1 text-stone-400">Classification Category *</label>
                <CustomSelect
                  className="w-full font-semibold text-stone-100 focus:border-amber-500/50 transition cursor-pointer"
                  menuClassName="divide-y divide-[#231e18] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-[#131110] [&::-webkit-scrollbar-thumb]:bg-[#2a241e] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-amber-500"
                  value={formData.category}
                  onChange={(val) => setFormData({ ...formData, category: val })}
                  options={[
                    { value: "", label: "Select Classification" },
                    ...CATEGORY_CHOICES.map((cat) => ({ value: cat, label: cat })),
                  ]}
                />
              </div>

              <div>
                <label className="block mb-1 text-stone-400">ISBN Code *</label>
                <input
                  className="w-full bg-[#131110] border border-[#2e2720] rounded-xl p-2.5 font-mono text-stone-100 focus:outline-none focus:border-amber-500/50 transition"
                  placeholder="e.g. 978-3-16-148410-0"
                  required
                  value={formData.isbn}
                  onChange={(e) => setFormData({ ...formData, isbn: e.target.value })}
                />
              </div>

              <div>
                <label className="block mb-1 text-stone-400">Barcode Identifier</label>
                <input
                  className="w-full bg-[#131110] border border-[#2e2720] rounded-xl p-2.5 font-mono text-stone-100 focus:outline-none focus:border-amber-500/50 transition"
                  placeholder="e.g. CAIS002934"
                  value={formData.barcode}
                  onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                />
              </div>

              <div>
                <label className="block mb-1 text-stone-400">Call Number</label>
                <input
                  className="w-full bg-[#131110] border border-[#2e2720] rounded-xl p-2.5 font-medium text-stone-100 focus:outline-none focus:border-amber-500/50 transition"
                  placeholder="e.g. QA76.9.D3"
                  value={formData.call_number}
                  onChange={(e) => setFormData({ ...formData, call_number: e.target.value })}
                />
              </div>

              <div>
                <label className="block mb-1 text-stone-400">Publication Year</label>
                <input
                  className="w-full bg-[#131110] border border-[#2e2720] rounded-xl p-2.5 font-medium text-stone-100 focus:outline-none focus:border-amber-500/50 transition"
                  placeholder="2026"
                  type="number"
                  value={formData.publication_year}
                  onChange={(e) => setFormData({ ...formData, publication_year: e.target.value })}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block mb-1 text-stone-400">Publisher Entity</label>
                <input
                  className="w-full bg-[#131110] border border-[#2e2720] rounded-xl p-2.5 font-medium text-stone-100 focus:outline-none focus:border-amber-500/50 transition"
                  placeholder="O'Reilly Media"
                  value={formData.publisher}
                  onChange={(e) => setFormData({ ...formData, publisher: e.target.value })}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block mb-1 text-stone-400">Asset Abstract Description</label>
                <textarea
                  rows={3}
                  className="w-full bg-[#131110] border border-[#2e2720] rounded-xl p-2.5 font-medium text-stone-100 focus:outline-none focus:border-amber-500/50 transition resize-none"
                  placeholder="Provide summary text description of physical item context..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="sm:col-span-2 pt-2">
                <button
                  type="submit"
                  className="w-full bg-amber-500 font-mono hover:bg-amber-400 text-stone-950 font-bold py-3 rounded-xl transition shadow-md shadow-amber-500/10"
                >
                  Save Book
                </button>
              </div>
            </form>
          </div>
        </NewBookModal>
      )}

      {bulkModalOpen && (
        <BulkUploadModal
          onClose={() => setBulkModalOpen(false)}
          onUploadComplete={() => fetchBooks(false)}
        />
      )}
    </AppLayout>
  );
}